import { useEffect, useState } from 'react';
import { Alert, Badge } from 'react-bootstrap';
import { DeployBlockNotice } from '@/components/clientFlags/ClientTags';
import { useAuth } from '@/contexts/AuthContext';
import { useClientFlags } from '@/hooks/useClientFlags';
import {
  DeployBlockedError,
  markDeploymentResolved,
  overrideDeploymentStatus,
  startDeployment,
  stopDeployment,
  stopGroupDeployment,
  waitForDeploymentRecord,
  type DeploymentRecord,
} from '@/services/deploymentService';
import type { Client } from '@/types';
import type { FailureInfo } from '@/utils/deployFailureStore';
import {
  groupDisplayName,
  imageDisplay,
  isGroupSummary,
  statusWord,
  waitPhrase,
  type ImagesByChannel,
} from '@/utils/deployStatus';
import { timeAgo } from '@/utils/relativeTime';
import { ConfirmDialog } from './ConfirmDialog';
import './deployments.css';

export interface RunAction {
  type: 'run-again' | 'stop' | 'mark-failed' | 'mark-resolved';
  record: DeploymentRecord;
}

interface RunActionDialogProps {
  /** The action waiting for a yes. Null closes the dialog. */
  action: RunAction | null;
  images?: ImagesByChannel;
  /** Known clients, to tell an internal client from a customer's. Without them a Dev image always needs the typed name. */
  clients?: Client[];
  /** For "Run again": a newer run of the same client, when there is one. */
  newerRun?: DeploymentRecord;
  /** For "Run again": the client has a deploy running now. */
  clientBusy?: boolean;
  /** For "Run again": why the run failed, to say how long to wait. */
  failure?: FailureInfo;
  onClose: () => void;
  onDone: (action: RunAction, result: { deploymentId?: string }) => void;
}

/**
 * Asks before any change to a run, says what the change does, then makes it.
 * One dialog for the three deployment pages, so the same action reads and
 * behaves the same wherever it is started.
 */
export function RunActionDialog({
  action,
  images,
  clients,
  newerRun,
  clientBusy = false,
  failure,
  onClose,
  onDone,
}: RunActionDialogProps) {
  const { user } = useAuth();
  const flags = useClientFlags();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Keep the last action on screen while the dialog fades out.
  const [shown, setShown] = useState<RunAction | null>(action);

  useEffect(() => {
    if (action) {
      setShown(action);
      setError(null);
    }
  }, [action]);

  if (!shown) return null;
  const { record, type } = shown;
  const group = isGroupSummary(record);
  const image = imageDisplay(record, images);
  const name = group ? groupDisplayName(record) : record.clientName;
  // "Run again" on a client marked do not deploy follows the rule for a single deploy: the reason
  // is shown, and a deploy needs the client's name typed. A plan changes nothing, so it only warns.
  const block = type === 'run-again' ? flags.deployBlock(record.clientName) : undefined;
  const blockedDeploy = Boolean(block) && record.mode !== 'plan';

  const confirm = async (note: string) => {
    const email = user?.email;
    if (!email) {
      setError('Your session has ended. Sign in again, then repeat this.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      let deploymentId: string | undefined;
      if (type === 'run-again') {
        const started = await startDeployment({
          clientName: record.clientName,
          imageTag: record.imageTag || '',
          // The deploy pipeline needs the channel. Rows from before channels existed are production.
          repository: record.repository ?? 'numa-deploy',
          initiatedBy: email,
          deploymentLabel: record.deploymentLabel || undefined,
          mode: record.mode === 'plan' ? 'plan' : 'deploy',
          // A member of a group run stays with its group, so the group page shows the second run.
          ...(record.groupRunId ? { groupRunId: record.groupRunId, groupName: record.groupName } : {}),
          // The person typed the client's name against this block. The start reads the block again.
          ...(blockedDeploy && block ? { acknowledgedBlockSetAt: block.setAt } : {}),
        });
        deploymentId = started.deploymentId;
        await waitForDeploymentRecord(deploymentId);
      } else if (type === 'stop') {
        if (group) await stopGroupDeployment(record, email);
        else await stopDeployment(record, email);
      } else if (type === 'mark-failed') {
        await overrideDeploymentStatus(record.deploymentId, 'failed', `manually failed by ${email}`, {
          clientName: record.clientName,
          recordStartedAt: record.startedAt,
        });
      } else {
        await markDeploymentResolved(record.deploymentId, email, note);
      }
      onDone(shown, { deploymentId });
    } catch (e) {
      // The client was blocked, or its block changed, after this page read the tags. Nothing
      // started. Read the tags again so the dialog shows the block as it stands.
      if (e instanceof DeployBlockedError) void flags.reload();
      else console.error(`Deploy action ${type} failed`, e);
      setError(e instanceof Error ? e.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const common = { show: Boolean(action), busy, error, onCancel: onClose, onConfirm: confirm };

  if (type === 'run-again') {
    const client = clients?.find((c) => c.name === record.clientName);
    // A Dev image on a customer's client is unusual enough to ask for the name. With no client list, assume it is one.
    const devToCustomer = image.dev && !(client && client.config.devInstance);
    const olderImage = newerRun && newerRun.imageTag !== record.imageTag;
    const wait = failure?.failure && failure.failure.retry === 'wait' ? waitPhrase(failure.failure.waitSeconds) : '';
    return (
      <ConfirmDialog
        {...common}
        title={blockedDeploy ? 'Deploy to a blocked client?' : 'Run this deploy again?'}
        confirmLabel={blockedDeploy ? 'Deploy anyway' : 'Run again'}
        variant={blockedDeploy ? 'danger' : 'primary'}
        // One typed name covers both checks: a blocked client, and a Dev image on a customer stack.
        typeToConfirm={devToCustomer || blockedDeploy ? record.clientName : undefined}
        blocked={
          clientBusy
            ? `A deploy for ${record.clientName} is running now. Run this again when that one ends.`
            : undefined
        }
      >
        <p>
          This starts a new {record.mode === 'plan' ? 'plan' : 'deploy'} for <strong>{record.clientName}</strong> with
          the same image and label.
        </p>
        <dl className="deploy-confirm__facts">
          <dt>Image</dt>
          <dd>
            <span className={image.named ? undefined : 'mono'} title={image.tag}>
              {image.label}
            </span>
            {image.dev && (
              <Badge bg="warning" className="ms-2">
                Dev
              </Badge>
            )}
          </dd>
          <dt>Label</dt>
          <dd>{record.deploymentLabel || 'None'}</dd>
          {record.endedAt && (
            <>
              <dt>Last ended</dt>
              <dd>{timeAgo(record.endedAt)}</dd>
            </>
          )}
        </dl>
        {block && (
          <>
            <DeployBlockNotice clientName={record.clientName} block={block} className="mt-3" />
            {blockedDeploy && (
              <p className="deploy-confirm__hint">
                Typing the name starts the deploy in spite of the block. The block stays on, and the activity log
                records that you went ahead.
              </p>
            )}
          </>
        )}
        {wait && (
          <Alert variant="info" className="mb-0 mt-3">
            This error clears by itself{wait}. A run started sooner can fail the same way.
          </Alert>
        )}
        {newerRun && (
          <Alert variant="warning" className="mb-0 mt-3">
            A newer run for this client exists: {statusWord(newerRun.status).label.toLowerCase()},{' '}
            {timeAgo(newerRun.startedAt)}.
            {olderImage && (
              <>
                {' '}
                It used image <span className="mono">{imageDisplay(newerRun, images).label}</span>. Running this again
                puts the older image back on the client.
              </>
            )}
          </Alert>
        )}
        {devToCustomer && (
          <Alert variant="danger" className="mb-0 mt-3">
            This is a Dev image. Dev images are built by hand from a branch and did not go through the release flow.
          </Alert>
        )}
      </ConfirmDialog>
    );
  }

  if (type === 'stop') {
    return (
      <ConfirmDialog
        {...common}
        title={group ? 'Stop this group run?' : 'Stop this deploy?'}
        confirmLabel={group ? 'Stop group run' : 'Stop deploy'}
        variant="danger"
      >
        {group ? (
          <p className="mb-0">
            No more clients of <strong>{name}</strong> start. A client that is deploying now can keep going: check each
            one on the group page.
          </p>
        ) : (
          <p className="mb-0">
            This stops the deploy for <strong>{name}</strong> and frees the client so it can be deployed again.
            Terraform can be part way through a change when it stops, so run the deploy again to finish the job.
          </p>
        )}
      </ConfirmDialog>
    );
  }

  if (type === 'mark-failed') {
    return (
      <ConfirmDialog {...common} title="Mark this run as failed?" confirmLabel="Mark failed" variant="danger">
        <p className="mb-0">
          Use this when the run for <strong>{name}</strong> is dead and still shows as running. It marks the run failed
          and frees the client so it can be deployed again. It does not stop a deploy that is still going: use Stop for
          that.
        </p>
      </ConfirmDialog>
    );
  }

  return (
    <ConfirmDialog
      {...common}
      title="Mark this failure as resolved?"
      confirmLabel="Mark resolved"
      noteLabel="What fixed it (optional)"
      notePlaceholder="A second run passed"
    >
      <p className="mb-0">
        The run for <strong>{name}</strong> stays in the history with its error and its real duration. It no longer
        counts as failed, and your name goes beside it.
      </p>
    </ConfirmDialog>
  );
}

export default RunActionDialog;
